export function makeC4dRigScript(): string {
  return `# MayoFlux Face Capture - Cinema 4D puppet rig builder
# Run this from Cinema 4D's Script Manager and select the capture.json file
# from the MayoFlux export package. It builds an animated face mesh, landmark
# nulls, body pose nulls when present, and a small set of keyframed puppet
# controls for rigging.

import json
import os

import c4d
from c4d import gui, storage


FACE_SCALE = (180.0, 130.0, 220.0)
POSE_SCALE = (220.0, 220.0, 220.0)
BAKE_CONTROL_KEYS = True

LANDMARK_LABELS = {
    1: "Nose",
    10: "Forehead",
    13: "UpperLip",
    14: "LowerLip",
    33: "LeftEyeOuter",
    61: "MouthLeft",
    152: "Chin",
    234: "LeftCheek",
    263: "RightEyeOuter",
    291: "MouthRight",
    454: "RightCheek",
}

POSE_LABELS = {
    0: "Nose",
    11: "LeftShoulder",
    12: "RightShoulder",
    13: "LeftElbow",
    14: "RightElbow",
    15: "LeftWrist",
    16: "RightWrist",
    23: "LeftHip",
    24: "RightHip",
    25: "LeftKnee",
    26: "RightKnee",
    27: "LeftAnkle",
    28: "RightAnkle",
}

CONTROL_MAP = [
    ("CTRL_Nose", "face", 1),
    ("CTRL_Forehead", "face", 10),
    ("CTRL_Chin", "face", 152),
    ("CTRL_Mouth_Left", "face", 61),
    ("CTRL_Mouth_Right", "face", 291),
    ("CTRL_Upper_Lip", "face", 13),
    ("CTRL_Lower_Lip", "face", 14),
    ("CTRL_Left_Eye", "face", 33),
    ("CTRL_Right_Eye", "face", 263),
    ("CTRL_Left_Cheek", "face", 234),
    ("CTRL_Right_Cheek", "face", 454),
    ("CTRL_Left_Shoulder", "pose", 11),
    ("CTRL_Right_Shoulder", "pose", 12),
    ("CTRL_Left_Wrist", "pose", 15),
    ("CTRL_Right_Wrist", "pose", 16),
]


def clamp_fps(value):
    try:
        fps = int(round(float(value)))
    except Exception:
        fps = 30
    return max(12, min(120, fps or 30))


def face_to_world(landmark):
    return (
        (float(landmark.get("x", 0.5)) - 0.5) * FACE_SCALE[0],
        (0.5 - float(landmark.get("y", 0.5))) * FACE_SCALE[1],
        -float(landmark.get("z", 0.0)) * FACE_SCALE[2],
    )


def pose_to_world(landmark):
    return (
        (float(landmark.get("x", 0.5)) - 0.5) * POSE_SCALE[0],
        (0.5 - float(landmark.get("y", 0.5))) * POSE_SCALE[1],
        -float(landmark.get("z", 0.0)) * POSE_SCALE[2],
    )


def compact_vec(vector):
    return (round(vector[0], 3), round(vector[1], 3), round(vector[2], 3))


def vector(value):
    return c4d.Vector(float(value[0]), float(value[1]), float(value[2]))


def make_null(name, position=(0.0, 0.0, 0.0)):
    obj = c4d.BaseObject(c4d.Onull)
    obj.SetName(name)
    obj.SetRelPos(vector(position))
    try:
        obj[c4d.NULLOBJECT_RADIUS] = 2.0
    except Exception:
        pass
    return obj


def insert_child(doc, obj, parent):
    doc.InsertObject(obj, parent=parent)
    return obj


def make_face_mesh(doc, parent, points, triangles):
    polygons = []
    for index in range(0, len(triangles) - 2, 3):
        a, b, c = int(triangles[index]), int(triangles[index + 1]), int(triangles[index + 2])
        if 0 <= a < len(points) and 0 <= b < len(points) and 0 <= c < len(points):
            polygons.append((a, b, c))

    mesh = c4d.PolygonObject(len(points), len(polygons))
    mesh.SetName("FaceMesh_Animated")
    mesh.SetAllPoints([vector(point) for point in points])
    for index, poly in enumerate(polygons):
        mesh.SetPolygon(index, c4d.CPolygon(poly[0], poly[1], poly[2], poly[2]))
    mesh.Message(c4d.MSG_UPDATE)
    insert_child(doc, mesh, parent)
    return mesh


def track_key(curve, frame, fps, value):
    result = curve.AddKey(c4d.BaseTime(frame, fps))
    key = result["key"] if isinstance(result, dict) else result
    key.SetValue(curve, float(value))
    try:
        key.SetInterpolation(curve, c4d.CINTERPOLATION_LINEAR)
    except Exception:
        pass


def set_position_keys(obj, positions, fps):
    axes = (
        (c4d.VECTOR_X, 0),
        (c4d.VECTOR_Y, 1),
        (c4d.VECTOR_Z, 2),
    )
    for axis_id, component in axes:
        desc_id = c4d.DescID(
            c4d.DescLevel(c4d.ID_BASEOBJECT_REL_POSITION, c4d.DTYPE_VECTOR, 0),
            c4d.DescLevel(axis_id, c4d.DTYPE_REAL, 0),
        )
        track = c4d.CTrack(obj, desc_id)
        obj.InsertTrackSorted(track)
        curve = track.GetCurve()
        for frame, position in enumerate(positions):
            track_key(curve, frame, fps, position[component])


def child_by_name(parent, name):
    child = parent.GetDown()
    while child:
        if child.GetName() == name:
            return child
        found = child_by_name(child, name)
        if found:
            return found
        child = child.GetNext()
    return None


def build_driver_code(face_frames, pose_frames, control_map):
    return """import c4d

FACE_FRAMES = %r
POSE_FRAMES = %r
CONTROL_MAP = %r
CACHE = {}

def v(value):
    return c4d.Vector(float(value[0]), float(value[1]), float(value[2]))

def child_by_name(parent, name):
    child = parent.GetDown()
    while child:
        if child.GetName() == name:
            return child
        found = child_by_name(child, name)
        if found:
            return found
        child = child.GetNext()
    return None

def ordered_children(parent, prefix, count):
    result = [None] * count
    if parent is None:
        return result
    child = parent.GetDown()
    while child:
        name = child.GetName()
        if name.startswith(prefix):
            try:
                index = int(name[len(prefix):len(prefix) + 3])
                if 0 <= index < count:
                    result[index] = child
            except Exception:
                pass
        child = child.GetNext()
    return result

def build_cache(root):
    face_group = child_by_name(root, "Face_Landmark_Nulls")
    pose_group = child_by_name(root, "BodyPose_Nulls")
    controls = child_by_name(root, "Puppet_Controls")
    return {
        "root": root,
        "mesh": child_by_name(root, "FaceMesh_Animated"),
        "face_nulls": ordered_children(face_group, "LM_", len(FACE_FRAMES[0]) if FACE_FRAMES else 0),
        "pose_nulls": ordered_children(pose_group, "POSE_", len(POSE_FRAMES[0]) if POSE_FRAMES else 0),
        "controls": [(child_by_name(controls, name), kind, index) for name, kind, index in CONTROL_MAP],
    }

def frame_index(doc):
    if doc is None or not FACE_FRAMES:
        return 0
    frame = int(round(doc.GetTime().GetFrame(doc.GetFps())))
    return max(0, min(len(FACE_FRAMES) - 1, frame))

def main():
    global CACHE
    root = op.GetObject()
    doc = root.GetDocument() if root else None
    if root is None or doc is None or not FACE_FRAMES:
        return
    if CACHE.get("root") != root:
        CACHE = build_cache(root)

    index = frame_index(doc)
    face_points = FACE_FRAMES[index]
    pose_points = POSE_FRAMES[index] if POSE_FRAMES and index < len(POSE_FRAMES) else []

    mesh = CACHE.get("mesh")
    if mesh and mesh.GetPointCount() == len(face_points):
        mesh.SetAllPoints([v(point) for point in face_points])
        mesh.Message(c4d.MSG_UPDATE)

    for point_index, obj in enumerate(CACHE.get("face_nulls", [])):
        if obj is not None and point_index < len(face_points):
            obj.SetRelPos(v(face_points[point_index]))

    for point_index, obj in enumerate(CACHE.get("pose_nulls", [])):
        if obj is not None and point_index < len(pose_points):
            obj.SetRelPos(v(pose_points[point_index]))

    for obj, kind, point_index in CACHE.get("controls", []):
        if obj is None:
            continue
        source = face_points if kind == "face" else pose_points
        if point_index < len(source):
            obj.SetRelPos(v(source[point_index]))
""" % (face_frames, pose_frames, control_map)


def add_python_driver(root, face_frames, pose_frames):
    tag = c4d.BaseTag(c4d.Tpython)
    tag.SetName("MayoFlux_Rig_Driver")
    tag[c4d.TPYTHON_CODE] = build_driver_code(face_frames, pose_frames, CONTROL_MAP)
    try:
        tag[c4d.TPYTHON_FRAME] = True
    except Exception:
        pass
    root.InsertTag(tag)


def load_capture():
    try:
        path = storage.LoadDialog(title="Select MayoFlux capture.json", flags=c4d.FILESELECT_LOAD, force_suffix="json")
    except TypeError:
        path = storage.LoadDialog()
    if not path:
        return None, None
    with open(path, "r", encoding="utf-8") as handle:
        return path, json.load(handle)


def first_pose_frame(frames):
    for frame in frames:
        points = frame.get("poseLandmarks") or []
        if points:
            return points
    return []


def main():
    path, data = load_capture()
    if not data:
        return

    frames = data.get("frames") or []
    if not frames:
        gui.MessageDialog("This capture.json has no frames.")
        return

    source = data.get("source") or {}
    fps = clamp_fps(source.get("fpsEstimate", 30))
    triangles = data.get("triangles") or []

    face_frames = [
        [compact_vec(face_to_world(point)) for point in frame.get("landmarks", [])]
        for frame in frames
    ]
    pose_frames = [
        [compact_vec(pose_to_world(point)) for point in (frame.get("poseLandmarks") or [])]
        for frame in frames
    ]
    has_pose = any(len(points) > 0 for points in pose_frames)

    first_face = face_frames[0]
    if not first_face:
        gui.MessageDialog("This capture.json has no face landmarks.")
        return

    doc = c4d.documents.GetActiveDocument()
    if doc is None:
        gui.MessageDialog("No active Cinema 4D document.")
        return

    root = make_null("MayoFlux_Puppet_Rig")
    face_group = make_null("Face_Landmark_Nulls")
    pose_group = make_null("BodyPose_Nulls")
    controls_group = make_null("Puppet_Controls")

    doc.StartUndo()
    doc.InsertObject(root)
    doc.AddUndo(c4d.UNDOTYPE_NEWOBJ, root)
    insert_child(doc, face_group, root)
    insert_child(doc, pose_group, root)
    insert_child(doc, controls_group, root)

    make_face_mesh(doc, root, first_face, triangles)

    for index, point in enumerate(first_face):
        label = LANDMARK_LABELS.get(index)
        name = "LM_%03d_%s" % (index, label) if label else "LM_%03d" % index
        insert_child(doc, make_null(name, point), face_group)

    pose_seed = first_pose_frame(frames)
    if has_pose and pose_seed:
        for index, point in enumerate([compact_vec(pose_to_world(item)) for item in pose_seed]):
            label = POSE_LABELS.get(index)
            name = "POSE_%03d_%s" % (index, label) if label else "POSE_%03d" % index
            insert_child(doc, make_null(name, point), pose_group)

    control_objects = []
    for name, kind, point_index in CONTROL_MAP:
        if kind == "face" and point_index < len(first_face):
            obj = make_null(name, first_face[point_index])
            insert_child(doc, obj, controls_group)
            control_objects.append((obj, kind, point_index))
        elif kind == "pose" and has_pose and pose_seed and point_index < len(pose_seed):
            obj = make_null(name, compact_vec(pose_to_world(pose_seed[point_index])))
            insert_child(doc, obj, controls_group)
            control_objects.append((obj, kind, point_index))

    if BAKE_CONTROL_KEYS:
        for obj, kind, point_index in control_objects:
            if kind == "face":
                positions = [points[point_index] for points in face_frames if point_index < len(points)]
            else:
                positions = [points[point_index] for points in pose_frames if point_index < len(points)]
            if positions:
                set_position_keys(obj, positions, fps)

    add_python_driver(root, face_frames, pose_frames if has_pose else [])

    doc.SetFps(fps)
    doc.SetMinTime(c4d.BaseTime(0, fps))
    doc.SetMaxTime(c4d.BaseTime(max(0, len(frames) - 1), fps))
    doc.SetLoopMinTime(c4d.BaseTime(0, fps))
    doc.SetLoopMaxTime(c4d.BaseTime(max(0, len(frames) - 1), fps))
    doc.EndUndo()
    c4d.EventAdd()

    gui.MessageDialog(
        "MayoFlux puppet rig built.\\n\\n"
        "Animated mesh: FaceMesh_Animated\\n"
        "Driven nulls: LM_### under Face_Landmark_Nulls\\n"
        "Rig controls: Puppet_Controls\\n\\n"
        "The mesh and landmark nulls are driven by the MayoFlux_Rig_Driver Python tag. "
        "Use Cinema 4D Bake Objects if you need baked keys on every generated null."
    )


if __name__ == "__main__":
    main()
`;
}
